/**
 * Intervene feature layout patterns — uses project-level tokens only.
 * Colors/radius/shadows live in src/theme/tokens/.
 */
import { colors } from '../../../theme/tokens/colors';
import { chat as defaultChat, status as defaultStatus } from '../../../theme/tokens/semantic';
import { whatsappMessageShadow } from '../../../theme/tokens/chatMessage';
import { radius, radiusPx } from '../../../theme/tokens/radius';
import { shadows } from '../../../theme/tokens/shadows';
import {
  controlHeights,
  density,
  spacing,
  spacingFunction,
} from '../../../theme/tokens/spacing';
import {
  CONTENT_HEIGHT,
  createSharedInputRoot,
  createCompactInputRoot,
  compactInputLabelSx,
  compactInputTextSx,
  disabledSurface,
  horizontalBleed,
  subtleSurface,
} from './layoutCommon';
import {
  CONTEXT_AREA_SPLIT,
  CONTEXT_DRAWER_WIDTH,
  CONTEXT_FLEX_RATIO,
  USER_LIST_WIDTH,
  interveneBubbleShape,
  interveneRadius,
} from './interveneLayoutTokens';

/**
 * Builds intervene layout styles from theme tokens.
 * Pass palette.chat / palette.status for runtime (tenant) theming via useInterveneSx().
 */
export function createInterveneSx(tokenOverrides = {}) {
  const chat = tokenOverrides.chat ?? defaultChat;
  const status = tokenOverrides.status ?? defaultStatus;
  const typography = tokenOverrides.typography ?? {};
  const typographyStyles = tokenOverrides.typographyStyles ?? {};
  const userListTokens = chat.userList ?? {};
  const messageTokens = chat.message ?? {};
  const sentTokens = messageTokens.sent ?? {};
  const receivedTokens = messageTokens.received ?? {};
  const innerTokens = messageTokens.inner ?? {};

  const controlRadius = interveneRadius.control;
  const pageTitle = typographyStyles.pageTitle || {};
  const captionText = typographyStyles.caption || {};

  const sharedInputRoot = createSharedInputRoot({ chat, typography });
  const compactInputRoot = createCompactInputRoot({ chat, typography });
  const compactLabelSx = compactInputLabelSx(chat, typography);
  const compactTextSx = compactInputTextSx(typography);

  return {
    layoutShell: {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 auto',
      alignSelf: 'stretch',
      minHeight: 0,
      height: '100%',
      bgcolor: chat.background,
      overflow: 'hidden',
      ...horizontalBleed,
      borderRadius: { md: interveneRadius.surface },
      border: { md: `1px solid ${chat.divider}` },
      boxShadow: { md: shadows.card },
      '& .MuiButton-root': { letterSpacing: 0 },
      '& .MuiIconButton-root': { transition: 'all 0.15s ease' },
    },
    panelShell: {
      height: '100%',
      bgcolor: chat.paper,
      overflow: 'hidden',
    },
    pageRoot: {
      display: 'flex',
      flexDirection: 'column',
      bgcolor: chat.paper,
      overflow: 'hidden',
      ...horizontalBleed,
      minHeight: CONTENT_HEIGHT,
      height: CONTENT_HEIGHT,
      maxHeight: CONTENT_HEIGHT,
    },
    sidebarSection: {
      px: 2,
      py: 1,
    },
    sidebarDivider: {
      borderBottom: `1px solid ${chat.divider}`,
      mx: 2,
    },
    inputField: {
      '& .MuiOutlinedInput-root': {
        ...sharedInputRoot,
        bgcolor: chat.paper,
        minHeight: controlHeights.md,
        borderRadius: interveneRadius.control,
        boxShadow: shadows.xs,
        '&.Mui-focused': {
          bgcolor: chat.paper,
          boxShadow: `0 0 0 3px ${chat.primaryMuted}`,
        },
      },
    },
    channelField: {
      '& .MuiOutlinedInput-root': {
        ...sharedInputRoot,
        bgcolor: chat.paper,
        fontWeight: typography.fontWeightMedium || 500,
        minHeight: controlHeights.md,
        borderRadius: interveneRadius.control,
        boxShadow: 'none',
      },
    },
    filterSearchField: {
      '& .MuiOutlinedInput-root': {
        ...sharedInputRoot,
        bgcolor: subtleSurface(chat),
        '&.Mui-focused': { bgcolor: chat.paper },
      },
    },
    filterTabBar: {
      display: 'flex',
      alignItems: 'stretch',
      width: '100%',
      minWidth: 0,
      gap: 0.25,
      bgcolor: userListTokens.filterTabsBackground || chat.primaryMuted,
      borderRadius: radiusPx.md,
      p: 0.25,
      border: `1px solid ${chat.divider}`,
      boxSizing: 'border-box',
      overflowX: 'auto',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    },
    filterTab: {
      flex: '1 1 0',
      minWidth: 'min-content',
      height: 28,
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      px: 0.375,
      py: 0,
      color: chat.textSecondary,
      borderRadius: radiusPx.sm,
      transition: 'color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
      display: 'inline-flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.25,
      lineHeight: 1,
      boxShadow: 'none',
      whiteSpace: 'nowrap',
      '&:hover': {
        color: chat.textPrimary,
        bgcolor: 'rgba(255,255,255,0.55)',
      },
      '&:focus-visible': {
        outline: `2px solid ${chat.primary}`,
        outlineOffset: 1,
      },
    },
    filterTabLabel: {
      flex: '0 0 auto',
      fontSize: typography.fontSizeXs || '11px',
      fontWeight: typography.fontWeightMedium || 500,
      whiteSpace: 'nowrap',
      lineHeight: 1,
    },
    filterTabBadge: {
      flex: '0 0 auto',
      fontSize: '10px',
      fontWeight: typography.fontWeightSemibold || 600,
      minWidth: 15,
      height: 15,
      px: 0.25,
      borderRadius: interveneRadius.chip,
      bgcolor: 'rgba(15, 23, 42, 0.06)',
      color: chat.textMuted,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
    },
    filterTabBadgeActive: {
      bgcolor: chat.primaryMuted,
      color: chat.primary,
    },
    filterTabActive: {
      color: chat.primary,
      bgcolor: chat.primarySurface,
      boxShadow: 'none',
      '&:hover': {
        color: chat.primary,
        bgcolor: chat.primarySurface,
      },
    },
    agentStatusToggle: {
      root: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.375,
        flexShrink: 0,
        height: controlHeights.sm,
        minHeight: controlHeights.sm,
      },
      statusSelect: {
        minWidth: 104,
        height: controlHeights.sm,
        fontSize: typography.fontSizeSm || '13px',
        '& .MuiOutlinedInput-root': {
          height: controlHeights.sm,
          minHeight: controlHeights.sm,
          bgcolor: chat.paper,
          borderRadius: radiusPx.md,
          boxSizing: 'border-box',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          top: spacing.size4,
          bottom: density.compact.spacing[1],
          borderColor: chat.divider,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: chat.textMuted,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: chat.primary,
          borderWidth: 1,
        },
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          py: '0 !important',
          pl: 1,
          pr: '28px !important',
          height: `${controlHeights.sm}px`,
          minHeight: 'unset !important',
          boxSizing: 'border-box',
        },
        '& .MuiSelect-icon': {
          top: 'calc(50% - 0.4em)',
        },
      },
      menuItem: {
        py: 0.75,
        px: 1.25,
      },
    },
    primaryButton: {
      borderRadius: controlRadius,
      textTransform: 'none',
      fontWeight: typography.fontWeightSemibold || 600,
      fontSize: typography.fontSizeMd || '14px',
      minHeight: 44,
      px: 2.25,
      py: 1.125,
      boxShadow: 'none',
      bgcolor: chat.primary,
      color: chat.primaryContrast,
      '&:hover': {
        bgcolor: chat.primaryDark,
        boxShadow: shadows.md,
      },
      '&.Mui-disabled': {
        bgcolor: disabledSurface(chat),
        color: chat.textMuted,
      },
    },
    iconCircle: {
      width: 56,
      height: 56,
      borderRadius: controlRadius,
      bgcolor: chat.primaryMuted,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: chat.primary,
    },
    emptyStateCard: {
      maxWidth: 440,
      mx: 'auto',
      p: 4,
      bgcolor: chat.paper,
      borderRadius: radiusPx.lg,
      boxShadow: shadows.card,
      border: `1px solid ${chat.divider}`,
      textAlign: 'center',
    },
    headerAction: {
      base: {
        borderRadius: interveneRadius.control,
        textTransform: 'none',
        fontWeight: typography.fontWeightMedium || 500,
        fontSize: typography.fontSizeSm || '13px',
        lineHeight: 1,
        minHeight: 36,
        px: 1.5,
        py: 0,
        boxShadow: 'none',
        flexShrink: 0,
        '& .MuiButton-startIcon': {
          marginRight: 0.375,
          marginLeft: 0,
          '& > *:nth-of-type(1)': { fontSize: 16 },
        },
        '&:hover': { boxShadow: 'none' },
      },
      secondary: {
        bgcolor: chat.paper,
        color: chat.textPrimary,
        border: `1px solid ${chat.divider}`,
        '&:hover': {
          bgcolor: chat.primaryMuted,
          borderColor: chat.divider,
        },
      },
      primary: {
        bgcolor: chat.paper,
        color: chat.primary,
        border: `1px solid ${chat.divider}`,
        '&:hover': {
          bgcolor: chat.primaryMuted,
          borderColor: chat.divider,
          boxShadow: 'none',
        },
      },
      status: {
        bgcolor: chat.primaryMuted,
        color: chat.textMuted,
        border: '1px solid transparent',
        cursor: 'default',
        pointerEvents: 'none',
        opacity: 0.9,
      },
      iconOnly: {
        minWidth: 36,
        width: 36,
        height: 36,
        minHeight: 36,
        p: 0,
        bgcolor: 'transparent',
        color: chat.textPrimary,
        border: '1px solid transparent',
        borderRadius: interveneRadius.control,
        boxShadow: 'none',
        '&:hover': {
          bgcolor: chat.primaryMuted,
          borderColor: chat.divider,
        },
        '&.Mui-disabled': {
          opacity: 0.45,
          color: chat.textMuted,
        },
      },
      iconTile: {
        minWidth: 36,
        width: 36,
        height: 36,
        minHeight: 36,
        p: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        bgcolor: chat.paper,
        color: chat.primary,
        border: `1px solid ${chat.divider}`,
        borderRadius: radiusPx.sm,
        boxShadow: shadows.xs,
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover:not(:disabled)': {
          bgcolor: chat.primaryMuted,
          borderColor: chat.primary,
        },
        '&:disabled': {
          opacity: 0.45,
          cursor: 'not-allowed',
          color: chat.textMuted,
        },
        '& .MuiSvgIcon-root': {
          fontSize: 18,
        },
      },
      iconTileCompact: {
        minWidth: controlHeights.sm,
        width: controlHeights.sm,
        height: controlHeights.sm,
        minHeight: controlHeights.sm,
        p: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        bgcolor: chat.paper,
        color: chat.primary,
        border: `1px solid ${chat.divider}`,
        borderRadius: radiusPx.md,
        boxShadow: shadows.xs,
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover:not(:disabled)': {
          bgcolor: chat.primaryMuted,
          borderColor: chat.primary,
        },
        '&:disabled': {
          opacity: 0.45,
          cursor: 'not-allowed',
          color: chat.textMuted,
        },
        '& .MuiSvgIcon-root': {
          fontSize: 16,
        },
      },
      iconTileGroup: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        flexShrink: 0,
      },
      group: {
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        flexWrap: 'nowrap',
        flexShrink: 0,
      },
      stateGroup: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.375,
        p: 0.375,
        bgcolor: chat.primaryMuted,
        borderRadius: interveneRadius.control,
        flexShrink: 0,
      },
      stateOption: {
        minWidth: 0,
        height: 30,
        minHeight: 30,
        px: 1.25,
        py: 0,
        border: 'none',
        borderRadius: radiusPx.sm,
        bgcolor: 'transparent',
        color: chat.textSecondary,
        fontSize: typography.fontSizeXs || '12px',
        fontWeight: typography.fontWeightMedium || 500,
        lineHeight: 1,
        textTransform: 'none',
        boxShadow: 'none',
        '&:hover': {
          bgcolor: 'rgba(255,255,255,0.6)',
          boxShadow: 'none',
        },
        '&.Mui-disabled': {
          opacity: 0.5,
          color: chat.textMuted,
        },
      },
      stateOptionActive: {
        bgcolor: chat.paper,
        color: chat.primary,
        boxShadow: shadows.xs,
        '&:hover': {
          bgcolor: chat.paper,
        },
      },
      menuPaper: {
        borderRadius: interveneRadius.control,
        border: `1px solid ${chat.divider}`,
        boxShadow: shadows.md,
        mt: 0.5,
        minWidth: 168,
      },
      menuItem: {
        fontSize: typography.fontSizeMd || '14px',
        color: chat.textPrimary,
        py: 1,
        px: 1.5,
        '&:hover': {
          bgcolor: chat.primaryMuted,
        },
      },
    },
    searchField: {
      root: {
        width: '100%',
        '& .MuiInputLabel-root': {
          fontSize: typography.fontSizeSm || '13px',
          color: chat.textSecondary,
          '&.Mui-focused': { color: chat.primary },
        },
        '& .MuiOutlinedInput-root': {
          ...sharedInputRoot,
          bgcolor: subtleSurface(chat),
          '&.Mui-focused': { bgcolor: chat.paper },
        },
        '& .MuiOutlinedInput-notchedOutline': {
          top: spacingFunction(0),
        },
        '& .MuiOutlinedInput-input': {
          py: 1,
        },
        '& .MuiAutocomplete-popupIndicator': { color: chat.textSecondary },
        '& .MuiAutocomplete-clearIndicator': { color: chat.textSecondary },
      },
      spaced: {
        mt: 1.5,
      },
      slotProps: {
        paper: {
          sx: {
            borderRadius: interveneRadius.control,
            border: `1px solid ${chat.divider}`,
            boxShadow: shadows.md,
            mt: 0.5,
          },
        },
        listbox: {
          sx: {
            py: 0.5,
            '& .MuiAutocomplete-option': {
              fontSize: typography.fontSizeSm || '13px',
              borderRadius: radiusPx.sm,
              mx: 0.5,
              my: 0.125,
              '&[aria-selected="true"]': {
                bgcolor: chat.primaryMuted,
              },
              '&.Mui-focused': {
                bgcolor: chat.primarySurface,
              },
            },
          },
        },
      },
    },
    composer: {
      outer: {
        flexShrink: 0,
        px: 2,
        pt: 1,
        pb: 1.5,
        bgcolor: chat.paper,
        borderTop: `1px solid ${chat.divider}`,
      },
      pill: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 0.5,
        px: 0.625,
        py: 0.375,
        minHeight: 42,
        bgcolor: chat.paper,
        borderRadius: interveneRadius.surface,
        border: `1px solid ${chat.divider}`,
        boxShadow: shadows.xs,
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        '&:focus-within': {
          borderColor: chat.primaryBorder,
          boxShadow: `0 0 0 2px ${chat.primaryMuted}`,
        },
      },
      pillExpired: {
        alignItems: 'center',
        minHeight: 40,
        bgcolor: status.warningSurface,
        borderColor: status.warningBorder,
        boxShadow: 'none',
        '&:focus-within': {
          borderColor: status.warningBorder,
          boxShadow: `0 0 0 2px ${status.warningBorder}`,
        },
      },
      attachButton: {
        flexShrink: 0,
        width: 36,
        height: 36,
        minWidth: 36,
        mb: 0.125,
        color: chat.textMuted,
        transition: 'transform 0.2s ease, color 0.15s ease, background-color 0.15s ease',
        '&:hover': {
          bgcolor: chat.primaryMuted,
          color: chat.primary,
        },
      },
      attachButtonExpired: {
        bgcolor: chat.primaryMuted,
        color: chat.primary,
        '&:hover': {
          bgcolor: chat.primarySurface,
          color: chat.primaryDark,
        },
      },
      attachButtonOpen: {
        transform: 'rotate(45deg)',
        color: chat.primary,
        bgcolor: chat.primaryMuted,
      },
      inputArea: {
        flex: 1,
        minWidth: 0,
        py: 0.375,
        mb: 0.125,
        '& .MuiInputBase-root': {
          fontSize: typography.fontSizeSm || '13px',
          lineHeight: 1.5,
          color: chat.textPrimary,
          '&::before, &::after': { display: 'none' },
        },
        '& .MuiInputBase-input': {
          p: 0,
          minHeight: 24,
          maxHeight: 88,
          overflowY: 'auto !important',
          '&::placeholder': {
            color: chat.textMuted,
            opacity: 1,
          },
        },
        '& .MuiInputBase-inputMultiline::-webkit-scrollbar': {
          width: '4px',
        },
      },
      expiredState: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 0.25,
        py: 0.25,
      },
      expiredIcon: {
        flexShrink: 0,
        fontSize: 18,
        color: chat.textMuted,
      },
      expiredCopy: {
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.125,
      },
      expiredTitle: {
        fontSize: typography.fontSizeSm || '13px',
        fontWeight: typography.fontWeightMedium || 500,
        color: chat.textSecondary,
        lineHeight: 1.3,
      },
      expiredHint: {
        fontSize: typography.fontSizeXs || '12px',
        color: chat.textMuted,
        lineHeight: 1.3,
      },
      idleCta: {
        flexShrink: 0,
        ml: 0.5,
        mb: 0.125,
        fontWeight: typography.fontWeightSemibold || 600,
        fontSize: typography.fontSizeSm || '13px',
        minHeight: 32,
        px: 1.5,
        borderRadius: interveneRadius.control,
      },
      actionButton: {
        flexShrink: 0,
        width: 36,
        height: 36,
        minWidth: 36,
        mb: 0.125,
        color: chat.textMuted,
        '&:hover': {
          bgcolor: chat.primaryMuted,
          color: chat.primary,
        },
      },
      sendButton: {
        flexShrink: 0,
        width: 36,
        height: 36,
        minWidth: 36,
        mb: 0.125,
        borderRadius: radiusPx.full,
        bgcolor: chat.primary,
        color: chat.primaryContrast,
        boxShadow: 'none',
        '&:hover': {
          bgcolor: chat.primaryDark,
          boxShadow: 'none',
        },
        '&.Mui-disabled': {
          bgcolor: disabledSurface(chat),
          color: chat.textMuted,
        },
      },
      fileChip: {
        mb: 0.625,
        px: 0.75,
        py: 0.375,
        bgcolor: chat.primarySurface,
        borderRadius: interveneRadius.control,
        border: `1px solid ${chat.primaryBorder}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 0.75,
      },
      attachMenu: {
        paper: {
          borderRadius: radiusPx.lg,
          border: `1px solid ${chat.divider}`,
          boxShadow: shadows.lg,
          mb: 1,
          overflow: 'hidden',
        },
        list: {
          py: 0.75,
          px: 0.5,
          minWidth: 248,
        },
        item: {
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          width: '100%',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          px: 1,
          py: 0.875,
          borderRadius: interveneRadius.control,
          textAlign: 'left',
          transition: 'background-color 0.12s ease',
          '&:hover': {
            bgcolor: chat.primaryMuted,
          },
        },
        iconCircle: {
          width: 42,
          height: 42,
          borderRadius: radius.full,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        },
        itemLabel: {
          fontSize: typography.fontSizeMd || '14px',
          fontWeight: typography.fontWeightMedium || 500,
          color: chat.textPrimary,
          lineHeight: 1.2,
        },
        itemCopy: {
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.125,
        },
        itemHint: {
          fontSize: typography.fontSizeXs || '12px',
          color: chat.textMuted,
          lineHeight: 1.25,
        },
      },
    },
    userList: {
      root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        bgcolor: userListTokens.surface || chat.paper,
        borderTopLeftRadius: { md: interveneRadius.surface },
        borderBottomLeftRadius: { md: interveneRadius.surface },
      },
      controls: {
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.size10,
        px: 1.5,
        pt: 1,
        pb: 0.75,
        borderBottom: `1px solid ${chat.divider}`,
      },
      selectorGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: 0.625,
        minWidth: 0,
        p: 0.75,
        bgcolor: subtleSurface(chat),
        borderRadius: radiusPx.md,
        border: `1px solid ${chat.divider}`,
        boxSizing: 'border-box',
      },
      selectorGridTwoCol: {
        gridTemplateColumns: 'minmax(0, 0.95fr) minmax(0, 1.05fr)',
        gap: 0.75,
        '@media (max-width:599px)': {
          gridTemplateColumns: '1fr',
        },
      },
      selectorCell: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      },
      selectorIcon: {
        fontSize: 16,
        color: userListTokens.iconColor || chat.primary,
      },
      compactFieldWrap: {
        minWidth: 0,
        width: '100%',
        '& > div > div:first-of-type': {
          mb: spacing.size1,
          minHeight: 'auto',
        },
        '& label': {
          fontSize: typography.fontSizeXs || '12px',
          fontWeight: typography.fontWeightMedium || 500,
          color: chat.textSecondary,
          lineHeight: 1.3,
          letterSpacing: 0,
          textTransform: 'none',
        },
        '& .MuiOutlinedInput-root': {
          minHeight: controlHeights.sm,
          bgcolor: chat.paper,
          fontWeight: typography.fontWeightRegular || 400,
          fontSize: typography.fontSizeSm || '13px',
          pl: 0.75,
          pr: 0.5,
          '& fieldset': {
            top:0,
            borderColor: chat.divider,
          },
          '&:hover fieldset': {
            borderColor: chat.textMuted,
          },
          '&.Mui-focused fieldset': {
            borderColor: chat.primary,
            borderWidth: 1,
          },
        },
        '& .MuiOutlinedInput-input, & .MuiAutocomplete-input': {
          py: 0.5,
          px: 0.25,
          fontSize: typography.fontSizeSm || '13px',
          textOverflow: 'ellipsis',
          minWidth: 0,
        },
        '& .MuiInputAdornment-root': {
          ml: 0,
          mr: 0.25,
          maxHeight: 24,
        },
        '& .MuiAutocomplete-endAdornment': {
          right: '6px !important',
        },
        '& .MuiAutocomplete-popupIndicator': {
          p: 0.25,
          color: chat.textMuted,
        },
      },
      searchStatusRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        width: '100%',
        minWidth: 0,
        minHeight: controlHeights.sm,
      },
      channelBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        minWidth: 0,
        '@media (max-width:767px)': {
          gap: 1.5,
        },
      },
      fieldLabel: {
        display: 'block',
        m: 0,
        fontSize: typography.fontSizeMd || '14px',
        fontWeight: typography.fontWeightMedium || 500,
        color: chat.textPrimary,
        lineHeight: 1.3,
        '@media (max-width:767px)': {
          pl: '40px',
          pt: '12px',
          mb: 0.5,
        },
      },
      titleRow: {
        minHeight: 76,
        px: 2.5,
        pt: 1,
        pb: 0.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      },
      title: {
        ...pageTitle,
        color: chat.textPrimary,
        letterSpacing: '-0.02em',
      },
      titleMeta: {
        ...captionText,
        color: chat.textSecondary,
        mt: 0.25,
      },
      filterButton: {
        color: chat.primary,
        bgcolor: chat.primaryMuted,
        '&:hover': { bgcolor: chat.primarySurface },
      },
      filterRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.25,
        minWidth: 0,
      },
      filterTabsWrap: {
        flex: 1,
        minWidth: 0,
      },
      channelLabel: {
        display: 'block',
        m: 0,
        fontSize: typography.fontSizeMd || '14px',
        fontWeight: typography.fontWeightMedium || 500,
        color: chat.textPrimary,
        lineHeight: 1.3,
      },
      channelIcon: {
        color: userListTokens.iconColor || chat.primary,
      },
      conversationSearch: {
        flex: 1,
        minWidth: 0,
        '& > div': {
          height: controlHeights.sm,
          minHeight: controlHeights.sm,
          boxSizing: 'border-box',
          borderRadius: radiusPx.md,
          bgcolor: subtleSurface(chat),
          borderColor: chat.divider,
          boxShadow: 'none',
          transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
          '&:focus-within': {
            bgcolor: chat.paper,
            borderColor: chat.primary,
            boxShadow: `0 0 0 3px ${chat.primaryMuted}`,
          },
        },
      },
      scrollArea: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        px: 1.5,
        pb: 1.5,
        pt: 0.5,
        '&::-webkit-scrollbar': { width: 5 },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: chat.divider,
          borderRadius: 3,
        },
      },
      loadingCenter: {
        display: 'flex',
        justifyContent: 'center',
        py: 6,
      },
      loadingMore: {
        display: 'flex',
        justifyContent: 'center',
        py: 2,
      },
      emptyState: {
        textAlign: 'center',
        py: 6,
        px: 2,
      },
      spinner: {
        color: chat.primary,
      },
      list: {
        display: 'flex',
        flexDirection: 'column',
        gap: userListTokens.itemGap || 0.5,
      },
    },
    userListItem: {
      button: {
        borderRadius: userListTokens.itemRadius || interveneRadius.control,
        py: userListTokens.itemPaddingY || 1,
        px: userListTokens.itemPaddingX || 1.25,
        minHeight: userListTokens.itemMinHeight || 72,
        transition:
          'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
        border: `1px solid ${userListTokens.itemBorder || chat.divider}`,
        bgcolor: userListTokens.itemBackground || chat.paper,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'flex-start',
        gap: userListTokens.contentGap || 1.25,
        minWidth: 0,
        '& .MuiTouchRipple-root': {
          borderRadius: userListTokens.itemRadius || interveneRadius.control,
        },
        '&.Mui-selected': {
          bgcolor: userListTokens.itemSelectedBackground || '#F0F2F5',
          borderColor: userListTokens.itemSelectedBorder || 'transparent',
          borderWidth: 1,
          boxShadow: 'none',
          '&:hover': {
            bgcolor: userListTokens.itemSelectedBackground || '#F0F2F5',
          },
        },
        '&:hover': {
          bgcolor: userListTokens.itemHoverBackground || '#F0F2F5',
          borderColor: userListTokens.itemHoverBorder || 'transparent',
          boxShadow: 'none',
        },
        '@media (max-width:768px)': {
          px: 1,
          py: 1,
          gap: userListTokens.contentGapMobile || '8px',
        },
      },
      listAvatar: {
        minWidth: userListTokens.avatarSize || 40,
        width: userListTokens.avatarSize || 40,
        height: userListTokens.avatarSize || 40,
        flex: `0 0 ${userListTokens.avatarSize || '40px'}`,
        alignSelf: 'center',
        m: 0,
      },
      avatarWrap: {
        position: 'relative',
        width: userListTokens.avatarSize || 40,
        height: userListTokens.avatarSize || 40,
        minWidth: userListTokens.avatarSize || 40,
        flex: `0 0 ${userListTokens.avatarSize || '40px'}`,
        mt: 0.125,
      },
      badgeDot: {
        width: userListTokens.presenceSize || 10,
        height: userListTokens.presenceSize || 10,
        minWidth: userListTokens.presenceSize || 10,
        minHeight: userListTokens.presenceSize || 10,
        borderRadius: radius.full,
      },
      avatar: {
        width: userListTokens.avatarSize || 40,
        height: userListTokens.avatarSize || 40,
        minWidth: userListTokens.avatarSize || 40,
        minHeight: userListTokens.avatarSize || 40,
        flexShrink: 0,
        fontWeight: typography.fontWeightMedium || 500,
        fontSize: userListTokens.avatarFontSize || typography.fontSizeSm || '13px',
        color: userListTokens.avatarColor || colors.white,
      },
      textRoot: {
        flex: 1,
        minWidth: 0,
        m: 0,
        overflow: 'hidden',
      },
      content: {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 0.375,
        textAlign: 'left',
        pt: 0.125,
      },
      primaryRow: {
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 0.75,
        textAlign: 'left',
      },
      name: {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        textAlign: 'left',
        fontWeight: typography.fontWeightMedium || 500,
        fontSize: userListTokens.userNameFontSize || typography.fontSizeMd || '14px',
        lineHeight: userListTokens.userNameLineHeight || typography.lineHeightHeading || 1.3,
        color: userListTokens.userNameColor || chat.textPrimary,
        letterSpacing: '-0.01em',
      },
      metaStack: {
        flexShrink: 0,
        display: 'inline-flex',
        flexDirection: 'row-reverse',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: userListTokens.metaGap || 0.75,
        whiteSpace: 'nowrap',
        ml: 0.5,
        '@media (max-width:768px)': {
          gap: userListTokens.metaGapMobile || 0.5,
          ml: 0.25,
        },
      },
      timeLabel: {
        flexShrink: 0,
        fontSize: userListTokens.metaFontSize || typography.fontSizeXs || '12px',
        fontWeight: typography.fontWeightRegular || 400,
        color: userListTokens.metaColor || chat.textMuted,
        lineHeight: '16px',
      },
      secondaryRow: {
        width: '100%',
        minWidth: 0,
        mt: userListTokens.previewMarginTop || 0.375,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: userListTokens.previewGap || 0.5,
        overflow: 'hidden',
        textAlign: 'left',
      },
      preview: {
        flex: 1,
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        display: 'block',
        textAlign: 'left',
        fontSize: userListTokens.descriptionFontSize || typography.fontSizeSm || '13px',
        fontWeight: typography.fontWeightRegular || 400,
        lineHeight: userListTokens.descriptionLineHeight || typography.lineHeightBody || 1.5,
        color: userListTokens.descriptionColor || chat.textSecondary,
      },
      unreadBadge: {
        flexShrink: 0,
        minWidth: 20,
        height: 20,
        px: 0.625,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '999px',
        bgcolor: chat.primary,
        color: chat.primaryContrast || '#fff',
        fontSize: '11px',
        fontWeight: typography.fontWeightSemibold || 600,
        lineHeight: 1,
      },
      expiredChip: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        height: userListTokens.expiredBadgeHeight || 20,
        px: userListTokens.expiredBadgePaddingX || 0.75,
        fontSize: userListTokens.expiredBadgeFontSize || '10px',
        lineHeight: 1.1,
        fontWeight: typography.fontWeightMedium || 500,
        borderRadius: '999px',
        borderColor: status.expiredBorder,
        color: status.expired,
        bgcolor: status.expiredBg,
        border: `1px solid ${status.expiredBorder}`,
        '@media (max-width:768px)': {
          px: 0.625,
          height: 18,
          fontSize: '10.4px',
        },
      },
    },
    chatHeader: {
      root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        px: 2,
        py: 0.75,
        minHeight: 52,
        flexShrink: 0,
        bgcolor: chat.paper,
        borderBottom: `1px solid ${chat.divider}`,
        position: 'sticky',
        top: 0,
        zIndex: 3,
        overflow: 'visible',
        '@media (max-width:767px)': {
          minHeight: 52,
          px: 1,
          py: 0.5,
          gap: 0.1,
          pl: '40px',
        },
      },
      leadingCluster: {
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        flex: '1 1 auto',
        gap: 1,
        '@media (max-width:767px)': {
          gap: 0.75,
        },
      },
      profileTrigger: {
        display: 'inline-flex',
        alignItems: 'center',
        flex: '1 1 auto',
        minWidth: 0,
        maxWidth: '100%',
        gap: 1,
        '@media (max-width:767px)': {
          gap: 0.75,
        },
        cursor: 'pointer',
        borderRadius: interveneRadius.control,
        py: 0,
        px: 0,
        mx: 0,
        border: 'none',
        bgcolor: 'transparent',
        color: 'inherit',
        font: 'inherit',
        textAlign: 'left',
        boxShadow: 'none',
        '&:hover': {
          bgcolor: 'transparent',
          boxShadow: 'none',
        },
        '&:focus-visible': {
          outline: `2px solid ${chat.primary}`,
          outlineOffset: 2,
        },
      },
      profileTriggerActive: {
        bgcolor: 'transparent',
        '&:hover': {
          bgcolor: 'transparent',
        },
      },
      avatar: {
        width: 40,
        height: 40,
        minWidth: 40,
        minHeight: 40,
        flex: '0 0 40px',
        fontWeight: typography.fontWeightMedium || 500,
        fontSize: typography.fontSizeSm || '13px',
        flexShrink: 0,
        '@media (max-width:600px)': {
          width: 36,
          height: 36,
          minWidth: 36,
          minHeight: 36,
          flex: '0 0 36px',
          fontSize: '13px',
        },
      },
      userInfo: {
        flex: '0 1 auto',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 0.25,
        overflow: 'hidden',
      },
      onlineBadge: {},
      nameRow: {
        display: 'flex',
        alignItems: 'baseline',
        flexWrap: 'nowrap',
        gap: 0.5,
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        '@media (max-width:767px)': {
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0,
        },
      },
      statusRow: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.625,
        minWidth: 0,
      },
      statusDot: {
        width: 7,
        height: 7,
        minWidth: 7,
        minHeight: 7,
        borderRadius: radius.full,
        flex: '0 0 7px',
      },
      statusDotOnline: {
        bgcolor: chat.online,
      },
      statusDotOffline: {
        bgcolor: chat.textMuted,
      },
      contact: {
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: 0,
        bgcolor: 'transparent',
      },
      userName: {
        fontSize: typography.fontSizeMd || '14px',
        fontWeight: typography.fontWeightSemibold || 600,
        lineHeight: '18px',
        color: chat.textPrimary,
        flex: '0 1 auto',
        minWidth: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      },
      phoneSuffix: {
        fontSize: typography.fontSizeMd || '14px',
        fontWeight: typography.fontWeightSemibold || 600,
        lineHeight: '18px',
        color: chat.textPrimary,
        flex: '0 1 auto',
        flexShrink: 1,
        minWidth: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        '@media (max-width:767px)': {
          fontSize: '12px',
          fontWeight: 500,
          color: chat.textSecondary,
          lineHeight: '16px',
        },
      },
      lastSeenText: {
        fontSize: typography.fontSizeXs || '12px',
        fontWeight: typography.fontWeightRegular || 400,
        lineHeight: '16px',
        color: chat.textMuted,
        minWidth: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      },
      sessionStatusRow: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        minWidth: 0,
        maxWidth: '100%',
      },
      sessionStatusText: {
        fontSize: typography.fontSizeXs || '12px',
        fontWeight: typography.fontWeightMedium || 500,
        lineHeight: '16px',
        color: chat.primary,
        minWidth: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      },
      sessionInfoButton: {
        p: 0.25,
        ml: 0.125,
        color: chat.textMuted,
        '&:hover': {
          bgcolor: chat.primarySurface || 'rgba(18, 140, 126, 0.08)',
          color: chat.primary,
        },
      },
      viewingChip: {
        ml: 0.5,
        height: 20,
        fontSize: 11,
        flexShrink: 0,
      },
      onlineText: {
        fontSize: typography.fontSizeXs || '12px',
        fontWeight: typography.fontWeightRegular || 400,
        lineHeight: '16px',
        color: chat.textMuted,
        flexShrink: 0,
      },
      offlineText: {
        fontSize: typography.fontSizeXs || '12px',
        fontWeight: typography.fontWeightRegular || 400,
        lineHeight: '16px',
        color: chat.textMuted,
        flexShrink: 0,
      },
      actions: {
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
        minWidth: 0,
        '@media (max-width:900px)': {
          gap: 0.5,
        },
        '@media (max-width:600px)': {
          '& .MuiButton-root': {
            minWidth: 0,
            px: 1,
            fontSize: '11px',
          },
        },
      },
      backButton: {
        flexShrink: 0,
        width: 34,
        height: 34,
        p: 0.5,
        borderRadius: radius.full,
        color: chat.textPrimary,
        '&:hover': {
          bgcolor: chat.primaryMuted,
        },
        '@media (max-width:767px)': {
          width: 32,
          height: 32,
          p: 0.5,
        },
      },
    },
    messageBubble: {
      row: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        marginBottom: 2,
      },
      rowGrouped: {
        marginBottom: 0.5,
      },
      rowMine: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        paddingLeft: { xs: '12%', sm: '18%' },
        paddingRight: { xs: 1.5, sm: 2 },
      },
      rowTheir: {
        flexDirection: 'row',
        justifyContent: 'flex-start',
        paddingRight: { xs: '12%', sm: '18%' },
        paddingLeft: { xs: 1.5, sm: 2 },
      },
      avatarSlot: {
        width: 32,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      },
      avatarSpacer: {
        width: 32,
        height: 32,
      },
      bubble: {
        maxWidth: '100%',
        minWidth: 72,
        padding: '5px 10px 7px',
        wordBreak: 'break-word',
        position: 'relative',
        transition: 'box-shadow 160ms ease',
        overflow: 'hidden',
        fontSize: typography.fontSizeMd || '14px',
        lineHeight: 1.4286,
        letterSpacing: '0.01em',
      },
      bubbleMine: {
        backgroundColor: sentTokens.bg || chat.outboundBg,
        color: sentTokens.text || '#FFFFFF',
        border: 'none',
        boxShadow: sentTokens.shadow || whatsappMessageShadow,
        padding: '10px 14px',
        fontWeight: typography.fontWeightRegular || 400,
        '& .MuiTypography-root': {
          color: 'inherit',
        },
        '& [data-inverted-surface="true"]': {
          color: receivedTokens.text || '#1C1E21',
        },
        '& [data-inverted-surface="true"] .MuiTypography-root': {
          color: 'inherit',
        },
        '& [data-inverted-surface="true"] [data-inverted-meta="true"]': {
          color: receivedTokens.meta || '#667781',
        },
        '& .MuiTypography-colorTextSecondary': {
          color: sentTokens.meta || 'rgba(255, 255, 255, 0.78)',
        },
        '& .MuiLink-root': {
          color: sentTokens.link || 'rgba(255, 255, 255, 0.95)',
        },
      },
      bubbleTheir: {
        backgroundColor: receivedTokens.bg || '#EEF2F6',
        color: receivedTokens.text || '#1C1E21',
        border: 'none',
        boxShadow: receivedTokens.shadow || 'none',
        padding: '10px 14px',
        fontWeight: typography.fontWeightRegular || 400,
        '& .MuiTypography-root': {
          color: 'inherit',
        },
        '& .MuiTypography-colorTextSecondary': {
          color: receivedTokens.meta || '#8A8D91',
        },
      },
      bubbleMineSingle: {
        borderRadius: interveneBubbleShape.mineSingle,
      },
      bubbleMineFirst: {
        borderRadius: interveneBubbleShape.mineFirst,
      },
      bubbleMineMiddle: {
        borderRadius: interveneBubbleShape.mineMiddle,
      },
      bubbleMineLast: {
        borderRadius: interveneBubbleShape.mineLast,
      },
      bubbleTheirSingle: {
        borderRadius: interveneBubbleShape.theirSingle,
      },
      bubbleTheirFirst: {
        borderRadius: interveneBubbleShape.theirFirst,
      },
      bubbleTheirMiddle: {
        borderRadius: interveneBubbleShape.theirMiddle,
      },
      bubbleTheirLast: {
        borderRadius: interveneBubbleShape.theirLast,
      },
      bubbleAttachmentNeutral: {
        p: 0,
        maxWidth: { xs: 228, sm: 268 },
        backgroundColor: 'transparent',
        border: 'none',
        boxShadow: 'none',
        overflow: 'visible',
      },
      bubbleAttachmentShape: {
        borderRadius: interveneBubbleShape.attachment,
      },
      metaBelowBubble: {
        mt: 0.375,
        mx: 0.25,
        display: 'flex',
        alignItems: 'center',
        gap: 0.375,
        fontSize: '11px',
        fontWeight: typography.fontWeightRegular || 400,
        color: chat.textMuted,
        lineHeight: 1,
      },
      metaBelowBubbleMine: {
        justifyContent: 'flex-end',
        color: receivedTokens.meta || '#8A8D91',
      },
      metaBelowBubbleTheir: {
        justifyContent: 'flex-start',
        color: receivedTokens.meta || '#8A8D91',
      },
      avatar: {
        width: 32,
        height: 32,
        fontSize: typography.fontSizeXs || '12px',
        fontWeight: typography.fontWeightSemibold || 600,
        flexShrink: 0,
        border: `2px solid ${chat.paper}`,
      },
      contentColumn: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        maxWidth: '100%',
      },
      contentColumnMine: {
        alignItems: 'flex-end',
      },
      contentColumnTheir: {
        alignItems: 'flex-start',
      },
      senderLabel: {
        fontWeight: typography.fontWeightSemibold || 600,
        mb: 0.375,
        ml: 0.5,
        color: '#128C7E',
        fontSize: typography.fontSizeXs || '12px',
        letterSpacing: '0.01em',
      },
      bubbleFooter: {
        mt: 0.125,
        pt: 0,
        gap: 0.375,
        flexWrap: 'nowrap',
        justifyContent: 'flex-end',
        alignItems: 'center',
        float: 'right',
        clear: 'both',
        ml: 'auto',
      },
      bubbleFooterMine: {
        color: sentTokens.meta || 'rgba(255, 255, 255, 0.78)',
      },
      bubbleFooterTheir: {
        color: receivedTokens.meta || '#8A8D91',
      },
      statusInline: {
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 14,
        lineHeight: 1,
        ml: 0.25,
      },
      metaStack: {
        mt: 0.4,
        mx: 0.5,
        color: chat.textMuted,
        alignItems: 'center',
      },
      metaText: {
        fontSize: '11px',
        fontWeight: typography.fontWeightRegular || 400,
        lineHeight: 1.1,
        opacity: 1,
      },
      statusSent: {
        color: 'inherit',
        fontSize: 13,
      },
      statusRead: {
        color: sentTokens.statusRead || '#53BDEB',
        fontSize: 13,
      },
    },
    messageFeed: {
      dateSeparator: {
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        my: 1.5,
        px: { xs: 1, sm: 2 },
      },
      dateSeparatorLine: {
        flex: 1,
        height: '1px',
        bgcolor: innerTokens.divider || chat.divider,
      },
      dateSeparatorLabel: {
        flexShrink: 0,
        fontSize: '11px',
        fontWeight: typography.fontWeightMedium || 500,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: messageTokens.feed?.dateSeparator || chat.textMuted,
        lineHeight: 1,
      },
    },
    profile: {
      emptyShell: {
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: chat.textSecondary,
      },
      root: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: chat.background,
      },
      header: {
        position: 'relative',
        px: 2,
        pt: 2,
        pb: 2,
        bgcolor: chat.paper,
        borderBottom: `1px solid ${chat.divider}`,
      },
      closeButton: {
        position: 'absolute',
        top: 10,
        right: 10,
        width: 32,
        height: 32,
        color: chat.textSecondary,
        bgcolor: 'transparent',
        '&:hover': {
          bgcolor: chat.primaryMuted,
          color: chat.textPrimary,
        },
      },
      headerContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.75,
        pt: 0.5,
        px: 1,
      },
      headerAvatar: {
        width: 56,
        height: 56,
        bgcolor: chat.primary,
        color: colors.white,
        fontSize: typography.fontSizeLg || '18px',
        fontWeight: typography.fontWeightSemibold || 600,
        boxShadow: `0 0 0 3px ${chat.paper}, 0 0 0 5px ${chat.primaryMuted}`,
      },
      headerName: {
        fontSize: typography.fontSizeMd || '15px',
        fontWeight: typography.fontWeightSemibold || 600,
        color: chat.textPrimary,
        lineHeight: 1.3,
        textAlign: 'center',
      },
      headerPhone: {
        fontSize: typography.fontSizeXs || '12px',
        fontWeight: typography.fontWeightMedium || 500,
        color: chat.textSecondary,
        px: 0.875,
        py: 0.25,
        borderRadius: interveneRadius.chip,
        bgcolor: chat.primaryMuted,
        letterSpacing: '0.01em',
      },
      body: {
        flex: 1,
        overflow: 'auto',
        p: 1.25,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        '&::-webkit-scrollbar': { width: 5 },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: chat.divider,
          borderRadius: 999,
        },
      },
      loadingOverlay: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: chat.overlay,
        zIndex: 1,
      },
      accordion: {
        border: `1px solid ${chat.divider}`,
        borderRadius: radiusPx.lg,
        overflow: 'hidden',
        bgcolor: chat.paper,
        boxShadow: shadows.xs,
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        '&:before': { display: 'none' },
        '&.Mui-expanded': {
          margin: 0,
          borderColor: chat.primaryBorder,
          boxShadow: `0 0 0 1px ${chat.primaryMuted}`,
        },
      },
      accordionSummary: {
        minHeight: 46,
        px: 1.25,
        '& .MuiAccordionSummary-content': {
          alignItems: 'center',
          my: 0.875,
        },
        '& .MuiAccordionSummary-expandIconWrapper': {
          color: chat.textMuted,
          transition: 'transform 0.2s ease',
        },
      },
      accordionTitle: {
        fontSize: typography.fontSizeSm || '13px',
        fontWeight: typography.fontWeightSemibold || 600,
        color: chat.textPrimary,
      },
      accordionIconWrap: {
        width: 32,
        height: 32,
        borderRadius: interveneRadius.control,
        bgcolor: chat.primaryMuted,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        mr: 1,
        flexShrink: 0,
      },
      accordionIcon: {
        color: chat.primary,
        fontSize: 18,
      },
      accordionDetails: {
        pt: 1.25,
        px: 1.25,
        pb: 1.25,
        borderTop: `1px solid ${chat.divider}`,
        bgcolor: chat.primarySurface,
      },
      infoRow: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.2fr)',
        gap: 0.75,
        alignItems: 'center',
        py: 0.75,
        px: 1,
        mb: 0.5,
        borderRadius: interveneRadius.control,
        bgcolor: chat.paper,
        border: `1px solid ${chat.divider}`,
        '&:last-of-type': {
          mb: 0,
        },
      },
      infoLabel: {
        fontSize: typography.fontSizeXs || '11px',
        fontWeight: typography.fontWeightMedium || 500,
        color: chat.textMuted,
        lineHeight: 1.4,
      },
      infoValue: {
        fontSize: typography.fontSizeSm || '13px',
        fontWeight: typography.fontWeightMedium || 500,
        color: chat.textPrimary,
        textAlign: 'right',
        lineHeight: 1.4,
        wordBreak: 'break-word',
      },
      infoEmpty: {
        fontSize: typography.fontSizeSm || '13px',
        color: chat.textSecondary,
        py: 0.5,
      },
      journey: {
        stepper: {
          '& .MuiStep-root': {
            pb: 1.25,
          },
          '& .MuiStepLabel-label': {
            mt: 0.25,
          },
          '& .MuiStepIcon-root': {
            width: 22,
            height: 22,
          },
          '& .MuiStepIcon-root.Mui-completed': { color: chat.primary },
          '& .MuiStepIcon-root.Mui-active': { color: chat.primary },
          '& .MuiStepConnector-line': {
            borderColor: chat.primaryBorder,
            minHeight: 16,
          },
        },
        stepTitle: {
          fontSize: typography.fontSizeSm || '13px',
          fontWeight: typography.fontWeightSemibold || 600,
          color: chat.textPrimary,
          lineHeight: 1.35,
        },
        stepMeta: {
          display: 'block',
          mt: 0.25,
          fontSize: typography.fontSizeXs || '11px',
          color: chat.textMuted,
          lineHeight: 1.35,
        },
        toggleButton: {
          mt: 0.5,
          px: 1.5,
          py: 0.375,
          minHeight: 30,
          fontSize: typography.fontSizeXs || '12px',
          fontWeight: typography.fontWeightSemibold || 600,
          textTransform: 'none',
          borderRadius: interveneRadius.control,
          bgcolor: chat.paper,
          color: chat.primary,
          border: `1px solid ${chat.primaryBorder}`,
          boxShadow: 'none',
          '&:hover': {
            bgcolor: chat.primaryMuted,
            boxShadow: 'none',
          },
        },
        emptyText: {
          py: 0.75,
          fontSize: typography.fontSizeSm || '13px',
          color: chat.textSecondary,
        },
      },
    },
    contextDrawer: {
      root: {
        height: '100%',
        minHeight: 0,
        width: '100%',
        flex: '0 0 auto',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: chat.background,
        borderLeft: `1px solid ${chat.divider}`,
      },
      inlineWidth: {
        flex: `${CONTEXT_FLEX_RATIO.drawer} 1 ${CONTEXT_AREA_SPLIT.drawer}`,
        maxWidth: CONTEXT_AREA_SPLIT.drawer,
        minWidth: 0,
      },
      chatColumnWithDrawer: {
        flex: `${CONTEXT_FLEX_RATIO.chat} 1 ${CONTEXT_AREA_SPLIT.chat}`,
        maxWidth: CONTEXT_AREA_SPLIT.chat,
        minWidth: 0,
      },
      campaignSelectorCard: {
        p: 1.25,
        mb: 1,
        flexShrink: 0,
        borderRadius: interveneRadius.surface,
        border: `1px solid ${chat.divider}`,
        bgcolor: chat.paper,
      },
      campaignSelectorRow: {
        display: 'flex',
        gap: 1.25,
        mb: 1.25,
        alignItems: 'flex-start',
      },
      campaignSelectorField: {
        flex: 1,
        minWidth: 0,
      },
      leadListScroll: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        mt: 0.5,
      },
      leadListScrollCompact: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        overscrollBehavior: 'contain',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: chat.divider,
          borderRadius: 999,
        },
      },
      leadListSection: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        p: 1.25,
        borderRadius: interveneRadius.surface,
        border: `1px solid ${chat.divider}`,
        bgcolor: chat.paper,
      },
      leadList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      },
      leadListItem: {
        border: `1px solid ${chat.divider}`,
        borderRadius: interveneRadius.control,
        bgcolor: chat.background,
        px: 1.25,
        py: 1,
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          bgcolor: chat.hoverSurface || 'action.hover',
        },
        '&.Mui-selected': {
          borderColor: chat.primary,
          bgcolor: chat.selectedSurface || 'rgba(63, 81, 181, 0.08)',
        },
        '&.Mui-selected:hover': {
          bgcolor: chat.selectedSurface || 'rgba(63, 81, 181, 0.1)',
        },
      },
      leadListLoading: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      },
      leadListEmpty: {
        py: 3,
        px: 0.5,
      },
      leadListFooter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 1.5,
      },
      leadListSectionHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        mb: 0.75,
        flexShrink: 0,
      },
      leadListSectionTitle: {
        fontSize: typography.fontSizeSm || '0.8125rem',
        fontWeight: typography.fontWeightSemibold || 600,
        color: chat.textPrimary,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
      },
      leadListSectionDescription: {
        mt: 0.25,
        color: chat.textSecondary,
        fontSize: typography.fontSizeXs || '0.75rem',
        lineHeight: 1.4,
      },
      leadListNewButton: {
        minHeight: 28,
        fontSize: typography.fontSizeXs || '0.75rem',
        px: 1,
        borderRadius: interveneRadius.control,
      },
      leadListChevron: {
        ml: 1,
        flexShrink: 0,
        color: chat.textSecondary,
        fontSize: 20,
      },
      leadFormSection: {
        minHeight: 0,
        flex: '1 1 0',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: `1px solid ${chat.divider}`,
        borderRadius: interveneRadius.surface,
        bgcolor: chat.paper,
      },
      leadFormHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 1.25,
        flexShrink: 0,
        borderBottom: `1px solid ${chat.divider}`,
        bgcolor: chat.paper,
      },
      leadFormHeading: {
        flex: 1,
        minWidth: 0,
      },
      leadFormBackButton: {
        flexShrink: 0,
        width: 32,
        height: 32,
        color: chat.textSecondary,
        '&:hover': {
          color: chat.textPrimary,
          bgcolor: chat.primaryMuted,
        },
      },
      leadFormTitleRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 0.75,
      },
      leadFormTitle: {
        color: chat.textPrimary,
        fontSize: typography.fontSizeMd || '0.875rem',
        fontWeight: typography.fontWeightSemibold || 600,
        lineHeight: 1.35,
      },
      leadFormDescription: {
        mt: 0.68,
        color: chat.textSecondary,
        fontSize: `calc(${typography.fontSizeXs || '0.75rem'} - 3px)`,
        lineHeight: 1.45,
      },
      leadFormModeChip: {
        height: 20,
        borderRadius: interveneRadius.chip,
        fontSize: '0.6875rem',
        fontWeight: 600,
        '& .MuiChip-label': {
          px: 0.75,
        },
      },
      leadFormActions: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        '& .MuiButton-root': {
          minHeight: 32,
          whiteSpace: 'nowrap',
          borderRadius: interveneRadius.control,
        },
      },
      phoneFields: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        mt: 2,
      },
      leadFormFields: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        overscrollBehavior: 'contain',
        p: 1.5,
        bgcolor: chat.background,
        scrollbarWidth: 'thin',
        scrollbarGutter: 'stable',
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: chat.divider,
          borderRadius: 999,
        },
      },
      leadFormFooter: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
        px: 1.5,
        py: 1.25,
        borderTop: `1px solid ${chat.divider}`,
        bgcolor: chat.paper,
        '& .MuiButton-root': {
          minHeight: 34,
          minWidth: 96,
        },
      },
      leadFormLoading: {
        minHeight: 160,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
      },
      leadFormEmpty: {
        minHeight: 140,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        px: 2,
        textAlign: 'center',
      },
      leadDetailsTab: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        height: '100%',
      },
      leadDetailsTabBody: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        gap: 1,
      },
      overlayPaper: {
        width: { xs: '100%', sm: CONTEXT_DRAWER_WIDTH.md },
        maxWidth: '100vw',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      },
      header: {
        position: 'relative',
        flexShrink: 0,
        px: 2,
        pt: 1.5,
        pb: 1,
        bgcolor: chat.paper,
        borderBottom: `1px solid ${chat.divider}`,
      },
      headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        minHeight: 36,
      },
      headerTitle: {
        fontSize: typography.fontSizeMd || '15px',
        fontWeight: typography.fontWeightSemibold || 600,
        color: chat.textPrimary,
        lineHeight: 1.3,
      },
      closeButton: {
        width: 32,
        height: 32,
        color: chat.textSecondary,
        bgcolor: 'transparent',
        '&:hover': {
          bgcolor: chat.primaryMuted,
          color: chat.textPrimary,
        },
      },
      identityRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mt: 1,
        pt: 1,
        borderTop: `1px solid ${chat.divider}`,
      },
      identityAvatar: {
        width: 36,
        height: 36,
        bgcolor: chat.primary,
        color: colors.white,
        fontSize: typography.fontSizeSm || '13px',
        fontWeight: typography.fontWeightSemibold || 600,
      },
      identityName: {
        fontSize: typography.fontSizeSm || '13px',
        fontWeight: typography.fontWeightSemibold || 600,
        color: chat.textPrimary,
        lineHeight: 1.3,
      },
      identityPhone: {
        fontSize: typography.fontSizeXs || '12px',
        color: chat.textSecondary,
      },
      tabsRoot: {
        flexShrink: 0,
        minHeight: 40,
        borderBottom: `1px solid ${chat.divider}`,
        bgcolor: chat.paper,
        '& .MuiTab-root': {
          minHeight: 40,
          minWidth: 'auto',
          px: 1.5,
          py: 0.75,
          fontSize: typography.fontSizeXs || '12px',
          fontWeight: typography.fontWeightMedium || 500,
          textTransform: 'none',
          color: chat.textSecondary,
          '&.Mui-selected': {
            color: chat.primary,
            fontWeight: typography.fontWeightSemibold || 600,
          },
        },
        '& .MuiTabs-indicator': {
          bgcolor: chat.primary,
          height: 2,
        },
      },
      tabPanel: {
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      },
      tabPanelScroll: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        overscrollBehavior: 'contain',
        p: 1.25,
      },
      tabPanelFill: {
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        p: 1.25,
      },
      tabPanelFooter: {
        flexShrink: 0,
        px: 1.25,
        py: 1,
        borderTop: `1px solid ${chat.divider}`,
        bgcolor: chat.paper,
        display: 'flex',
        gap: 1,
        justifyContent: 'flex-end',
      },
      emptyShell: {
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: chat.textSecondary,
        flex: 1,
      },
      userListColumn: {
        height: '100%',
        minHeight: 0,
        flex: { xs: '1 1 auto', md: '0 0 auto' },
        overflow: 'hidden',
        borderRight: { md: `1px solid ${chat.divider}` },
        display: { xs: 'flex', md: 'flex' },
        flexDirection: 'column',
        transition: (theme) =>
          theme.transitions.create(['width', 'min-width', 'opacity'], {
            duration: theme.transitions.duration.short,
          }),
      },
      userListWidth: {
        width: { xs: '100%', md: USER_LIST_WIDTH.md, xl: USER_LIST_WIDTH.xl },
        minWidth: { xs: '100%', md: USER_LIST_WIDTH.md, xl: USER_LIST_WIDTH.xl },
      },
      userListCollapsed: {
        width: 0,
        minWidth: 0,
        opacity: 0,
        pointerEvents: 'none',
        borderRight: 'none',
      },
      overviewCard: {
        p: 1.25,
        mb: 1,
        borderRadius: interveneRadius.surface,
        border: `1px solid ${chat.divider}`,
        bgcolor: chat.paper,
      },
      overviewCardTitle: {
        fontSize: typography.fontSizeXs || '12px',
        fontWeight: typography.fontWeightSemibold || 600,
        color: chat.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        mb: 0.75,
      },
      overviewGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 0.75,
      },
      overviewLabel: {
        fontSize: typography.fontSizeXs || '12px',
        color: chat.textSecondary,
      },
      overviewValue: {
        fontSize: typography.fontSizeSm || '13px',
        fontWeight: typography.fontWeightMedium || 500,
        color: chat.textPrimary,
        wordBreak: 'break-word',
      },
      statChip: {
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.25,
        borderRadius: interveneRadius.chip,
        fontSize: typography.fontSizeXs || '12px',
        fontWeight: typography.fontWeightMedium || 500,
      },
    },
    dialog: {
      paper: {
        borderRadius: radiusPx.lg,
        border: `1px solid ${chat.divider}`,
        boxShadow: shadows.lg,
        overflow: 'hidden',
      },
      templatePaper: {
        width: '100%',
        maxWidth: 460,
        m: 2,
      },
      title: {
        fontWeight: typography.fontWeightSemibold || 600,
        fontSize: typography.fontSizeMd || '15px',
        color: chat.textPrimary,
        px: 2.5,
        pt: 2,
        pb: 0.75,
      },
      content: {
        px: 2.5,
        pt: 0.25,
        pb: 1.5,
      },
      contentCenter: {
        px: 3,
        pt: 0.5,
        pb: 3,
        display: 'flex',
        justifyContent: 'center',
      },
      contentFlush: {
        p: 0,
      },
      actions: {
        px: 2.5,
        py: 1.5,
        gap: 0.75,
      },
      agentOption: {
        row: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          width: '100%',
          minWidth: 0,
        },
        name: {
          flex: 1,
          minWidth: 0,
          fontWeight: typography.fontWeightMedium || 500,
          color: chat.textPrimary,
        },
      },
      field: {
        mt: 1,
        '& .MuiInputLabel-root': compactLabelSx,
        '& .MuiOutlinedInput-root': {
          ...compactInputRoot,
          bgcolor: subtleSurface(chat),
          '&.Mui-focused': { bgcolor: chat.paper },
          '&.Mui-disabled': {
            bgcolor: chat.primaryMuted || subtleSurface(chat),
            '& fieldset': { borderColor: chat.divider },
          },
        },
        '& .MuiOutlinedInput-notchedOutline': {
          top: spacingFunction(0),
        },
        '& .MuiOutlinedInput-input': compactTextSx,
        '& .MuiFormHelperText-root': {
          fontSize: typography.fontSizeXs || '11px',
          mt: spacing.size1,
          mx: 0,
          color: chat.textMuted,
          lineHeight: 1.3,
        },
        '& .MuiFormLabel-root': {
          top: -spacing.size2,
        },
      },
      searchField: {
        root: {
          width: '100%',
          '& .MuiInputLabel-root': compactLabelSx,
          '& .MuiOutlinedInput-root': {
            ...compactInputRoot,
            bgcolor: subtleSurface(chat),
            '&.Mui-focused': { bgcolor: chat.paper },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            top: spacingFunction(0),
          },
          '& .MuiOutlinedInput-input': compactTextSx,
          '& .MuiAutocomplete-popupIndicator': { color: chat.textSecondary, p: 0.25 },
          '& .MuiAutocomplete-clearIndicator': { color: chat.textSecondary, p: 0.25 },
        },
        spaced: {
          mt: 1,
        },
      },
      fieldSpaced: {
        mt: 2,
      },
      cancelButton: {
        textTransform: 'none',
        fontWeight: typography.fontWeightMedium || 500,
        fontSize: typography.fontSizeSm || '13px',
        borderRadius: interveneRadius.control,
        px: 1.5,
        py: 0.5,
        minHeight: controlHeights.sm,
        '&:hover': {
          bgcolor: chat.primaryMuted,
        },
      },
      submitButton: {
        textTransform: 'none',
        fontWeight: typography.fontWeightSemibold || 600,
        fontSize: typography.fontSizeSm || '13px',
        borderRadius: interveneRadius.control,
        px: 2,
        py: 0.5,
        minHeight: controlHeights.sm,
        boxShadow: 'none',
        bgcolor: chat.primary,
        color: colors.white,
        '&:hover': {
          bgcolor: chat.primaryDark,
          boxShadow: 'none',
        },
        '&.Mui-disabled': {
          bgcolor: disabledSurface(chat),
          color: chat.textMuted,
        },
      },
      requiredMark: {
        color: 'error.main',
      },
      chooseFileButton: {
        textTransform: 'none',
        fontWeight: typography.fontWeightSemibold || 600,
        fontSize: typography.fontSizeSm || '13px',
        borderRadius: interveneRadius.control,
        boxShadow: 'none',
        bgcolor: chat.primary,
        color: colors.white,
        '&:hover': {
          bgcolor: chat.primaryDark,
          boxShadow: 'none',
        },
      },
      voiceRecorder: {
        root: {
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          py: 1,
          '& .audio-recorder': {
            bgcolor: chat.primarySurface,
            boxShadow: shadows.md,
            border: `1px solid ${chat.divider}`,
          },
          '& .audio-recorder-mic': {
            color: chat.primary,
          },
          '& .audio-recorder-timer, & .audio-recorder-status': {
            color: chat.textPrimary,
            fontFamily: 'inherit',
            fontSize: typography.fontSizeSm || '13px',
          },
          '& .audio-recorder-status-dot': {
            bgcolor: status.expired,
          },
          '& .audio-recorder-options': {
            color: chat.primary,
          },
          '& .audio-recorder.recording': {
            bgcolor: chat.paper,
            border: `1px solid ${chat.primaryBorder}`,
          },
        },
      },
    },
  };
}

/** Static default — prefer useInterveneSx() in components for theme-aware styles. */
export const interveneSx = createInterveneSx();
